import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { CommunityResponse } from "@bw/shared/types/generated";
import { useFeed } from "@bw/shared/hooks";
import { useFacilityCommunity } from "../../dropin/hooks/useFacilityCommunity";
import { MaterialSocialPostCard } from "../../../common/components/MaterialSocialPostCard";
import { FeedSkeleton } from "../../social/components/FeedSkeleton";

const GALLERY_CAP = 6;

/** Read-only preview of the facility's real community feed. */
export function FacilityPostGallery({ facilityId }: { facilityId: string }) {
  const communityQ = useFacilityCommunity(facilityId);
  const community: CommunityResponse | null = communityQ.data ?? null;
  const feedQ = useFeed(
    {
      scope: "community",
      communityId: community?.community_id ?? "",
      limit: GALLERY_CAP,
    },
    { enabled: Boolean(community?.community_id) },
  );

  if (communityQ.isLoading || communityQ.isError || !community) return null;

  return (
    <Stack
      component="section"
      aria-labelledby="facility-gallery-title"
      spacing={2}
    >
      <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }} id="facility-gallery-title">
        From the club
      </Typography>
      <FacilityFeed feedQ={feedQ} communityId={community.community_id} />
    </Stack>
  );
}

function FacilityFeed({
  feedQ,
  communityId,
}: {
  feedQ: ReturnType<typeof useFeed>;
  communityId: string;
}) {
  if (feedQ.isLoading) return <FeedSkeleton />;

  if (feedQ.isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load this club&apos;s posts. Please try again.
      </Alert>
    );
  }

  // This surface is intentionally a preview. Enforce the cap at render time as
  // well as in the request so a malformed response cannot expand the page.
  const posts = feedQ.posts.slice(0, GALLERY_CAP);
  const communityHref = `/community/communities/${communityId}`;

  if (posts.length === 0) {
    return (
      <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
        <Typography color="text.secondary">No posts yet.</Typography>
        <Button component={RouterLink} to={communityHref} size="small">
          View community
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack
        component="ul"
        spacing={1.5}
        sx={{ p: 0, m: 0, listStyle: "none" }}
      >
        {posts.map((post) => (
          <Stack component="li" key={post.post_id}>
            <MaterialSocialPostCard
              post={post}
              detailPath={(id) => `/community/posts/${id}`}
              profilePath={(uid) => `/players/${uid}`}
            />
          </Stack>
        ))}
      </Stack>
      <Button
        component={RouterLink}
        to={communityHref}
        size="small"
        sx={{ alignSelf: "flex-start" }}
      >
        View community
      </Button>
    </Stack>
  );
}
